import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Camera, CheckCircle2, IdCard, Loader2, Fish } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pitch fishing passes (lodges fish free) — set by the park: £6/£10/£15/£20 for 1–4 rods per day.
const FISHING_PRICES = { 1: 6, 2: 10, 3: 15, 4: 20 };

const fmtDate = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

const fmtDay = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";

/** Photo picker with a small preview once chosen. */
const PhotoField = ({ label, hint, icon: Icon, file, onChange, alreadyHave, testId }) => {
  const [preview, setPreview] = useState(null);
  const pick = (e) => {
    const f = e.target.files?.[0] || null;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
    onChange(f);
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">{label}</p>
      <label
        className="flex items-center gap-3 border border-dashed border-[#B76E79]/60 rounded-xl px-4 py-3.5 cursor-pointer hover:bg-[#F5EFE6]/60 transition"
        data-testid={`${testId}-drop`}
      >
        {preview ? (
          <img src={preview} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#DDD1BE]" />
        ) : (
          <span className="w-12 h-12 rounded-lg bg-[#F5EFE6] flex items-center justify-center text-[#B76E79]">
            <Icon size={20} />
          </span>
        )}
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-[#14100F] truncate">{file ? file.name : "Tap to take or choose a photo"}</span>
          <span className="block text-[11px] text-[#6A5F58] mt-0.5">
            {file ? "Tap to replace" : alreadyHave ? "Already on file — tap to replace" : hint}
          </span>
        </span>
        <Camera size={16} className="text-[#B76E79] shrink-0" />
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testId} />
      </label>
    </div>
  );
};

/**
 * Guest self check-in (/check-in/:token) — hotel-style form sent by link.
 * One named section per party member (count prefilled from the booking);
 * each guest says whether they'll be fishing — only anglers are asked for a
 * licence photo. Lead guest adds contact + emergency numbers and photo ID.
 */
const GuestCheckIn = () => {
  const { token } = useParams();
  const universal = !token; // /check-in without a token — channel guests identify their stay
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const [count, setCount] = useState(2);
  const [party, setParty] = useState([]); // [{name, fishing, license(File|null), hasLicense(bool)}]
  const [phone, setPhone] = useState("");
  const [emergency, setEmergency] = useState("");
  const [reg, setReg] = useState("");
  const [idType, setIdType] = useState("");
  const [idDoc, setIdDoc] = useState(null);
  const [fishingSel, setFishingSel] = useState({}); // {date: rods}
  const [payBusy, setPayBusy] = useState(false);
  const [paidFlash, setPaidFlash] = useState(false);

  // Universal mode: identifier step state
  const [units, setUnits] = useState([]);
  const [identName, setIdentName] = useState("");
  const [identDate, setIdentDate] = useState("");
  const [identUnit, setIdentUnit] = useState("");
  const [identBusy, setIdentBusy] = useState(false);
  const [matched, setMatched] = useState(true);

  const buildParty = (n, prev = [], saved = []) =>
    Array.from({ length: n }, (_, i) => {
      const existing = prev[i];
      if (existing) return existing;
      const s = saved[i];
      return {
        name: s?.name || "",
        fishing: !!s?.fishing,
        license: null,
        hasLicense: !!s?.has_license,
      };
    });

  const fetchInfo = async () => {
    const { data } = await axios.get(`${API}/checkin/${token}`);
    setInfo(data);
    const saved = data.party || [];
    const n = Math.max(1, Math.min(data.guests || 2, 8));
    setCount(n);
    const initial = buildParty(n, [], saved);
    if (initial[0] && !initial[0].name) initial[0] = { ...initial[0], name: data.guest_name || "" };
    setParty(initial);
    setPhone(data.guest_phone || "");
    setEmergency(data.emergency_phone || "");
    setReg(data.vehicle_reg || "");
    setIdType(data.id_type || "");
    const sel = {};
    (data.fishing || []).forEach((f) => { if (f.rods > 0) sel[f.date] = f.rods; });
    setFishingSel(sel);
    return data;
  };

  /** Universal identifier step — name + arrival date + where they're staying. */
  const identify = async (e) => {
    e.preventDefault();
    if (identBusy) return;
    setIdentBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkin/lookup`, {
        guest_name: identName,
        arrival_date: identDate,
        unit_id: identUnit,
      });
      setInfo({
        ...data,
        guest_name: identName,
        guest_phone: "",
        emergency_phone: "",
        vehicle_reg: "",
        submitted: false,
        id_type: "",
        has_id_document: false,
        party: [],
        fishing: [],
        amount_due: 0,
        balance_due: 0,
        fishing_due: 0,
        payment_pending: false,
      });
      const n = Math.max(1, Math.min(data.guests || 2, 8));
      setCount(n);
      const initial = buildParty(n, [], []);
      initial[0] = { ...initial[0], name: identName };
      setParty(initial);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setIdentBusy(false);
    }
  };

  useEffect(() => {
    let disposed = false;
    (async () => {
      if (universal) {
        try {
          const { data } = await axios.get(`${API}/checkin/units`);
          if (!disposed) setUnits(data.units || []);
        } catch { /* identifier step still works with a fallback option */ }
        return;
      }
      try {
        const justPaid = new URLSearchParams(window.location.search).get("paid") === "1";
        const data = await fetchInfo();
        if (disposed) return;
        if (justPaid) {
          setPaidFlash(true);
          // Square can take a beat to mark the order complete — one gentle retry.
          if (data.payment_pending && data.amount_due > 0) {
            setTimeout(async () => { if (!disposed) { try { await fetchInfo(); } catch { /* ignore */ } } }, 3500);
          }
        }
      } catch (e) {
        if (!disposed) {
          const detail = e?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "This check-in link is invalid or has expired.");
        }
      }
    })();
    return () => { disposed = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const changeCount = (n) => {
    setCount(n);
    setParty((prev) => buildParty(n, prev, info?.party || []));
  };

  const setMember = (i, patch) =>
    setParty((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("guest_name", party[0]?.name || "");
      fd.append("guests", String(count));
      fd.append("guest_phone", phone);
      fd.append("emergency_phone", emergency);
      fd.append("vehicle_reg", reg);
      fd.append("id_type", idType);
      fd.append("party", JSON.stringify(party.map(({ name, fishing }) => ({ name, fishing }))));
      fd.append("fishing", JSON.stringify(
        Object.entries(fishingSel).filter(([, rods]) => rods > 0).map(([date, rods]) => ({ date, rods }))
      ));
      party.forEach((m, i) => {
        if (m.fishing && m.license) fd.append(`license_${i}`, m.license);
      });
      if (idDoc) fd.append("id_document", idDoc);
      if (universal) {
        fd.append("arrival_date", identDate);
        fd.append("unit_id", identUnit);
        const { data: res } = await axios.post(`${API}/checkin`, fd);
        setMatched(!!res.matched);
      } else {
        await axios.post(`${API}/checkin/${token}`, fd);
        try { await fetchInfo(); } catch { /* amounts refresh next visit */ }
      }
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const anyoneFishing = party.some((m) => m.fishing);
  const fishingTotal = Object.values(fishingSel).reduce((n, r) => n + (info?.chargeable_fishing ? FISHING_PRICES[r] || 0 : 0), 0);

  const payNow = async () => {
    if (payBusy) return;
    setPayBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkin/${token}/pay`);
      window.location.assign(data.url); // off to secure Square checkout
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't start the secure payment — please try again.");
      setPayBusy(false);
    }
  };

  /** Balance + fishing summary with a Square pay button — soft gate, guests may settle at reception instead. */
  const renderPayBox = () => (
    <div className="border border-[#DDD1BE] bg-[#F5EFE6] rounded-xl p-4 space-y-2.5 text-left" data-testid="checkin-pay-box">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58]">To settle</p>
      <div className="text-sm text-[#14100F]">
        {info.balance_due > 0 && (
          <p className="flex justify-between py-0.5">Stay balance <span className="font-semibold">£{info.balance_due.toFixed(2)}</span></p>
        )}
        {info.fishing_due > 0 && (
          <p className="flex justify-between py-0.5">Fishing passes <span className="font-semibold">£{info.fishing_due.toFixed(2)}</span></p>
        )}
        <p className="flex justify-between pt-1.5 mt-1.5 border-t border-[#DDD1BE] font-semibold">
          Total <span>£{info.amount_due.toFixed(2)}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={payNow}
        disabled={payBusy}
        className="w-full h-11 rounded-full bg-[#3B8C4E] text-white hover:bg-[#0F0E0E] transition text-[11px] font-brand tracking-[0.14em] uppercase disabled:opacity-50 inline-flex items-center justify-center gap-2"
        data-testid="checkin-pay-now"
      >
        {payBusy && <Loader2 size={13} className="animate-spin" />}
        Pay £{info.amount_due.toFixed(2)} securely
      </button>
      <p className="text-[11px] text-[#6A5F58] text-center leading-relaxed">
        Secure card payment by Square — or pay at reception when you arrive; the barrier opens once you're on site.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex flex-col" data-testid="checkin-page">
      <header className="bg-[#0F0E0E] py-6 px-6 text-center">
        <p className="font-serif text-[#F5EFE6] text-xl tracking-[0.28em] uppercase">Walnut Lakes</p>
        <p className="text-[#6A5F58] text-[10px] tracking-[0.22em] uppercase mt-1.5">Holiday Park · Lincolnshire</p>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden shadow-sm">
          {error && !info && !universal ? (
            <div className="p-8 text-center" data-testid="checkin-invalid">
              <AlertCircle size={28} className="mx-auto text-[#B76E79]" />
              <h1 className="font-serif text-2xl text-[#14100F] mt-4">This link isn't active</h1>
              <p className="text-sm text-[#6A5F58] mt-3 leading-relaxed">{error}</p>
              <p className="text-xs text-[#6A5F58] mt-4">
                Need help? Call Justina on <strong>07982 844201</strong> or email{" "}
                <a href="mailto:justina@walnutlakes.co.uk" className="text-[#B76E79] underline">justina@walnutlakes.co.uk</a>
              </p>
            </div>
          ) : universal && !info ? (
            <form onSubmit={identify} className="p-6 md:p-8 space-y-5" data-testid="checkin-identify-form">
              <div>
                <h1 className="font-serif text-2xl text-[#14100F]">Online check-in</h1>
                <p className="text-sm text-[#6A5F58] mt-1.5 leading-relaxed">
                  Booked through Booking.com, Pitchup or another site? You're in the right place — tell us which stay is yours.
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Lead guest name</p>
                <Input value={identName} onChange={(e) => setIdentName(e.target.value)} required maxLength={80} placeholder="The name on the booking" className="h-11" data-testid="checkin-identify-name" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Arrival date</p>
                <Input type="date" value={identDate} onChange={(e) => setIdentDate(e.target.value)} required className="h-11" data-testid="checkin-identify-date" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Where are you staying?</p>
                <select
                  value={identUnit}
                  onChange={(e) => setIdentUnit(e.target.value)}
                  required
                  className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="checkin-identify-unit"
                >
                  <option value="">Choose…</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                  {units.length === 0 && <option value="camping">Camping / touring pitch</option>}
                </select>
              </div>
              {error && (
                <p className="text-sm text-[#B76E79] bg-[#B76E79]/10 rounded-lg px-3 py-2" data-testid="checkin-identify-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={identBusy}
                className="w-full h-12 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.16em] uppercase disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="checkin-identify-submit"
              >
                {identBusy && <Loader2 size={14} className="animate-spin" />}
                Continue to check-in
              </button>
            </form>
          ) : !info ? (
            <div className="p-12 flex items-center justify-center gap-2 text-[#6A5F58] text-sm" data-testid="checkin-loading">
              <Loader2 size={16} className="animate-spin" /> Loading your check-in…
            </div>
          ) : done ? (
            <div className="p-8 text-center" data-testid="checkin-success">
              <CheckCircle2 size={32} className="mx-auto text-[#3B8C4E]" />
              <h1 className="font-serif text-2xl text-[#14100F] mt-4">You're checked in, {(party[0]?.name || "friend").split(" ")[0]}</h1>
              {paidFlash && info.amount_due <= 0 && (
                <p className="mt-3 text-sm font-semibold text-[#3B8C4E] bg-[#3B8C4E]/10 rounded-lg px-3 py-2" data-testid="checkin-paid-banner">
                  Payment received — thank you. You're all set.
                </p>
              )}
              {paidFlash && info.payment_pending && info.amount_due > 0 && (
                <p className="mt-3 text-sm text-[#6A5F58] bg-[#F5EFE6] rounded-lg px-3 py-2" data-testid="checkin-confirming-banner">
                  Payment received — just confirming with the bank, this can take a few seconds…
                </p>
              )}
              <p className="text-sm text-[#6A5F58] mt-3 leading-relaxed">
                {universal && !matched
                  ? "Thanks — we have everything we need. We'll match your details to your booking at reception, so arrival will be quick."
                  : `Thanks — we have everything we need for your party of ${count}. See you at the park; just come to reception when you arrive and we'll get you settled quickly.`}
              </p>
              {!universal && info.amount_due > 0 && <div className="mt-5">{renderPayBox()}</div>}
              <p className="text-xs text-[#6A5F58] mt-4">
                {info.unit_label} · {fmtDate(info.start_date)} → {fmtDate(info.end_date)}
              </p>
              <Link to="/" className="inline-block mt-6 text-[10px] uppercase tracking-[0.18em] text-[#B76E79] hover:underline">
                Back to walnutlakes.co.uk
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 md:p-8 space-y-6" data-testid="checkin-form">
              <div>
                <h1 className="font-serif text-2xl text-[#14100F]">Online check-in</h1>
                <p className="text-sm text-[#6A5F58] mt-1.5 leading-relaxed">
                  {info.unit_label} · {fmtDate(info.start_date)} → {fmtDate(info.end_date)}
                </p>
                {info.submitted && (
                  <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-[#3B8C4E] bg-[#3B8C4E]/10 rounded-full px-3 py-1.5 inline-block" data-testid="checkin-update-note">
                    Already completed — you can update anything below
                  </p>
                )}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">How many people are staying?</p>
                <select
                  value={count}
                  onChange={(e) => changeCount(Number(e.target.value))}
                  className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="checkin-guests"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#6A5F58] mt-1">Booked for {info.guests} — change it if that's wrong.</p>
              </div>

              {party.map((m, i) => (
                <div key={i} className="border border-[#EFE6D8] rounded-xl p-4 space-y-4" data-testid={`checkin-guest-section-${i}`}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-brand">
                    Guest {i + 1}{i === 0 ? " · lead guest" : ""}
                  </p>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Full name</p>
                    <Input
                      value={m.name}
                      onChange={(e) => setMember(i, { name: e.target.value })}
                      required
                      maxLength={80}
                      placeholder={i === 0 ? "Your full name" : `Guest ${i + 1}'s full name`}
                      className="h-11"
                      data-testid={`checkin-guest-name-${i}`}
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Will {i === 0 ? "you" : "they"} be fishing our lakes?</p>
                    <div className="flex gap-2">
                      {[["no", "Not fishing"], ["yes", "Fishing"]].map(([v, label]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setMember(i, { fishing: v === "yes" })}
                          className={`flex-1 h-10 rounded-full text-[10px] font-brand tracking-[0.12em] uppercase transition border ${
                            m.fishing === (v === "yes")
                              ? "bg-[#0F0E0E] text-[#F5EFE6] border-[#0F0E0E]"
                              : "border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6]"
                          }`}
                          data-testid={`checkin-guest-fishing-${v}-${i}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {m.fishing && (
                    <PhotoField
                      label={`Fishing licence — ${m.name.split(" ")[0] || `guest ${i + 1}`}`}
                      hint="EA rod licence photo"
                      icon={Fish}
                      file={m.license}
                      onChange={(f) => setMember(i, { license: f })}
                      alreadyHave={m.hasLicense}
                      testId={`checkin-license-input-${i}`}
                    />
                  )}
                  {i === 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Your contact number</p>
                      <Input value={phone} onChange={(e) => setPhone(e.target.value)} required type="tel" maxLength={30} placeholder="07…" className="h-11" data-testid="checkin-phone" />
                    </div>
                  )}
                </div>
              ))}

              {anyoneFishing && (
                <div className="border border-[#EFE6D8] rounded-xl p-4 space-y-3" data-testid="checkin-fishing-passes">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-brand">Fishing day passes</p>
                  {info.chargeable_fishing ? (
                    <p className="text-[11px] text-[#6A5F58] leading-relaxed">
                      Per day: 1 rod £6 · 2 rods £10 · 3 rods £15 · 4 rods £20. Pay below with anything else you owe, or at reception.
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#6A5F58] leading-relaxed">
                      Fishing is complimentary with your lodge — just tell us the days and rods so we know who's on the lakes.
                    </p>
                  )}
                  <div className="space-y-2">
                    {(info.stay_days || []).map((d) => (
                      <div key={d} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-[#14100F]">{fmtDay(d)}</span>
                        <select
                          value={fishingSel[d] || 0}
                          onChange={(e) => setFishingSel((prev) => ({ ...prev, [d]: Number(e.target.value) }))}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          data-testid={`checkin-fishing-day-${d}`}
                        >
                          <option value={0}>No fishing</option>
                          {[1, 2, 3, 4].map((r) => (
                            <option key={r} value={r}>
                              {r} rod{r > 1 ? "s" : ""}{info.chargeable_fishing ? ` — £${FISHING_PRICES[r]}` : " — included"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  {info.chargeable_fishing && fishingTotal > 0 && (
                    <p className="text-sm font-semibold text-[#14100F]" data-testid="checkin-fishing-total">
                      Fishing total: £{fishingTotal.toFixed(2)}
                    </p>
                  )}
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Emergency contact number</p>
                <Input value={emergency} onChange={(e) => setEmergency(e.target.value)} required type="tel" maxLength={30} placeholder="Someone we can call if needed" className="h-11" data-testid="checkin-emergency" />
                <p className="text-[11px] text-[#6A5F58] mt-1">Someone not staying with you, just in case.</p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Vehicle registration</p>
                <div className="flex">
                  <input
                    value={reg}
                    onChange={(e) => setReg(e.target.value.toUpperCase())}
                    maxLength={12}
                    placeholder="AB12 CDE"
                    autoComplete="off"
                    className="w-full h-12 rounded-lg border-2 border-[#1A1A1A] bg-[#FFBF00] px-4 text-center font-mono text-lg font-bold tracking-[0.2em] text-[#1A1A1A] placeholder-[#1A1A1A]/40 uppercase focus:outline-none focus:ring-2 focus:ring-[#B76E79]"
                    data-testid="checkin-vehicle-reg"
                  />
                </div>
                <p className="text-[11px] text-[#6A5F58] mt-1">So we can spot your car on the park — leave blank if you're not driving.</p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-1.5">Photo ID you'll bring</p>
                <select
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                  className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="checkin-id-type"
                >
                  <option value="">Choose…</option>
                  <option value="driving_licence">Driving licence</option>
                  <option value="passport">Passport</option>
                  <option value="other">Other photo ID</option>
                </select>
              </div>
              <PhotoField
                label="Photo ID"
                hint="Driving licence or passport photo page"
                icon={IdCard}
                file={idDoc}
                onChange={setIdDoc}
                alreadyHave={info.has_id_document}
                testId="checkin-id-input"
              />

              {anyoneFishing && (
                <p className="text-[11px] text-[#6A5F58] leading-relaxed bg-[#F5EFE6] rounded-lg px-3 py-2.5">
                  Anyone 13 or over fishing needs an Environment Agency rod licence — a quick photo of it keeps your arrival speedy.
                </p>
              )}

              {error && (
                <p className="text-sm text-[#B76E79] bg-[#B76E79]/10 rounded-lg px-3 py-2" data-testid="checkin-error">{error}</p>
              )}

              {!universal && info.amount_due > 0 && renderPayBox()}

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.16em] uppercase disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="checkin-submit"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {info.submitted ? "Update check-in" : "Complete check-in"}
              </button>
              <p className="text-[11px] text-[#6A5F58] text-center leading-relaxed">
                Your details and photos are stored securely and only used for your stay.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default GuestCheckIn;
